import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { toast } from 'sonner';
import jsPDF from 'jspdf';
import ServiceChargeModal from './components/ServiceChargeModal';
import ProcessingModal from './components/ProcessingModal';
import EmailReceiptModal from './components/EmailReceiptModal';
import ReadAloudButton from './components/ReadAloudButton';

const API = process.env.REACT_APP_BACKEND_URL;

function BVADMPage({ user }) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  
  // Check where user came from for navigation
  const fromLocation = searchParams.get('from');
  const returnPath = fromLocation === 'activity' ? '/user_dashboard' : '/homepage';
  
  // Dashboard metrics state
  const [reports, setReports] = useState([]);
  const [transactions, setTransactions] = useState([]);
  
  const [formData, setFormData] = useState({
    manuscript_title: '',
    manuscript_file: null,
    genre: '',
    target_audience: '',
    publication_goals: '',
    marketing_budget: '',
    timeline: '',
    previous_works: '',
    author_platform: ''
  });
  
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState('');
  const [processingProgress, setProcessingProgress] = useState(0);
  const [currentStep, setCurrentStep] = useState('');
  const [progressSteps, setProgressSteps] = useState([
    { text: 'Analyzing manuscript', completed: false, current: false },
    { text: 'Generating viability analysis', completed: false, current: false },
    { text: 'Formatting results', completed: false, current: false }
  ]);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentInfo, setPaymentInfo] = useState(null);
  const [showEmailReceiptModal, setShowEmailReceiptModal] = useState(false);
  const [lastTransactionId, setLastTransactionId] = useState(null);
  
  // Load report if report ID is in URL
  useEffect(() => {
    const reportId = searchParams.get('report');
    if (reportId) {
      loadReport(reportId);
    }
  }, [searchParams.get('report')]);
  
  // Fetch dashboard metrics
  useEffect(() => {
    const fetchDashboardData = async () => {
      try {
        // Fetch all BVADM reports
        const reportsResponse = await axios.get(`${API}/api/bvadm/reports`, {
          withCredentials: true
        });
        setReports(reportsResponse.data || []);

        // Fetch transaction history
        const transactionsResponse = await axios.get(`${API}/api/payments/transactions`, {
          withCredentials: true
        });
        const transactionsData = transactionsResponse.data;
        setTransactions(Array.isArray(transactionsData) ? transactionsData : []);
      } catch (err) {
        console.error('Failed to fetch dashboard data:', err);
      }
    };

    if (user) {
      fetchDashboardData();
    }
  }, [user]);
  
  const loadReport = async (reportId) => {
    try {
      const response = await axios.get(`${API}/api/bvadm/reports/${reportId}`, {
        withCredentials: true
      });
      
      const reportData = response.data;
      
      // Update document title
      document.title = `${reportData.title || reportData.manuscript_title} | BVADM Report`;
      setResult(reportData);
    } catch (err) {
      console.error('Failed to load report:', err);
      setError('Failed to load report. Please try again.');
      navigate('/user_dashboard');
    }
  };
  
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  const handleFileChange = (e) => {
    const file = e.target.files[0];
    if (file) {
      // Validate file type
      const allowedTypes = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document', 'text/plain'];
      if (!allowedTypes.includes(file.type)) {
        setError('Please upload a PDF, Word document, or text file.');
        return;
      }
      
      // Validate file size (50MB limit)
      if (file.size > 50 * 1024 * 1024) {
        setError('File size must be less than 50MB.');
        return;
      }
      
      setFormData(prev => ({ ...prev, manuscript_file: file }));
      setError('');
    }
  };
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    console.log('🔍 BVADM handleSubmit called');
    console.log('User role:', user?.role);
    console.log('Form data:', { title: formData.manuscript_title, hasFile: !!formData.manuscript_file });
    
    if (!formData.manuscript_title.trim() || !formData.manuscript_file) {
      setError('Please provide both a manuscript title and upload a file.');
      return;
    }
    
    setLoading(true);
    setError('');
    
    const submissionData = {
      name: 'Book Viability & Digital Marketing Analysis',
      bookTitle: formData.manuscript_title || 'BVADM Analysis'
    };
    
    try {
      // Step 1: Check if user needs to pay BEFORE processing (only for À La Carte users)
      console.log('Checking payment requirement for role:', user?.role);
      if (user?.role === 'a_la_carte') {
        console.log('✅ Showing payment modal for a_la_carte user');
        // IMPORTANT: Show payment authorization modal BEFORE any processing or charging
        // Pricing: $12.00 for À La Carte users (defined in backend)
        setPaymentInfo({
          amount: 12.00,
          currency: 'USD',
          description: 'Book Viability & Digital Marketing Analysis - Comprehensive AI-powered analysis of your manuscript',
          submission_data: submissionData,
          name: 'Book Viability & Digital Marketing Analysis',
          bookTitle: formData.manuscript_title || 'BVADM Analysis'
        });
        setShowPaymentModal(true);
        setLoading(false);
        return; // Stop here - wait for user to confirm payment
      }
      
      // Process the analysis (for subscribers, admins, beta testers who don't need payment confirmation)
      console.log('🚀 Starting analysis for non-payment user');
      await processGeneration();
      
    } catch (err) {
      console.error('❌ BVADM submission error:', err);
      setError(err.response?.data?.detail || err.message || 'An error occurred while analyzing your manuscript.');
      setLoading(false);
    }
  };
  
  const processGeneration = async () => {
    try {
      // Step 1: Upload and analyze
      setCurrentStep('Uploading and analyzing manuscript...');
      setProgressSteps([
        { text: 'Analyzing manuscript', completed: false, current: true },
        { text: 'Generating viability analysis', completed: false, current: false },
        { text: 'Formatting results', completed: false, current: false }
      ]);
      setProcessingProgress(10);
      
      const formDataToSend = new FormData();
      
      // Map form fields to backend expected names
      formDataToSend.append('title', formData.manuscript_title || '');
      formDataToSend.append('file', formData.manuscript_file);
      formDataToSend.append('author_name', user?.name || 'Author');
      formDataToSend.append('genre', formData.genre || '');
      formDataToSend.append('subtitle', '');
      formDataToSend.append('publisher', '');
      formDataToSend.append('sub_genre', formData.target_audience || '');
      formDataToSend.append('use_intelligence', 'true');
      
      setProcessingProgress(25);
      
      // Step 2: Generate analysis
      setCurrentStep('Generating comprehensive viability analysis...');
      setProgressSteps([
        { text: 'Analyzing manuscript', completed: true, current: false },
        { text: 'Generating viability analysis', completed: false, current: true },
        { text: 'Formatting results', completed: false, current: false }
      ]);
      setProcessingProgress(40);
      
      const response = await axios.post(`${API}/api/bvadm/upload-and-analyze`, formDataToSend, {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
        withCredentials: true
      });
      
      // Step 3: Formatting
      setCurrentStep('Finalizing viability report...');
      setProgressSteps([
        { text: 'Analyzing manuscript', completed: true, current: false },
        { text: 'Generating viability analysis', completed: true, current: false },
        { text: 'Formatting results', completed: false, current: true }
      ]);
      setProcessingProgress(85);
      
      setResult(response.data);
      
      // Complete
      setProgressSteps([
        { text: 'Analyzing manuscript', completed: true, current: false },
        { text: 'Generating viability analysis', completed: true, current: false },
        { text: 'Formatting results', completed: true, current: false }
      ]);
      setProcessingProgress(100);
      setCurrentStep('Complete!');
      
      // Update URL without reloading the page
      const newUrl = new URL(window.location);
      newUrl.searchParams.set('report', response.data.id);
      window.history.pushState({}, '', newUrl);
      
      // Update document title
      document.title = `${response.data.title || response.data.manuscript_title} | BVADM Report`;
      
      // Save transaction ID if provided
      if (response.data.transaction_id) {
        setLastTransactionId(response.data.transaction_id);
      }
      
      toast.success('Book viability analysis completed!');
      
      // Refresh dashboard stats after successful service completion
      window.dispatchEvent(new CustomEvent('refreshDashboardStats'));
      
    } catch (err) {
      console.error('Analysis error:', err);
      setError(err.response?.data?.detail || err.message || 'Analysis failed');
    } finally {
      setLoading(false);
    }
  };
  
  const handlePaymentSuccess = async (paymentData) => {
    setShowPaymentModal(false);
    setLoading(true);
    
    try {
      // IMPORTANT: User confirmed willingness to pay
      // BUT actual charge happens in backend AFTER successful analysis
      // This ensures users are only charged for successfully completed services
      
      await processGeneration();
      
      // Note: Backend will create transaction record after successful completion
      // Transaction ID will be in the response from processGeneration
      
    } catch (error) {
      console.error('Post-payment analysis error:', error);
      setError(error.response?.data?.detail || error.message || 'Analysis failed. You have NOT been charged.');
      setLoading(false);
    }
  };
  
  const exportToPDF = () => {
    if (!result) {
      toast.error('No analysis data to export');
      return;
    }
    
    try {
      const pdf = new jsPDF();
      
      // Set up fonts and spacing
      const pageWidth = 190; // PDF width minus margins
      const leftMargin = 10;
      let yPosition = 20;
      
      // Header
      pdf.setFontSize(20);
      pdf.setFont(undefined, 'bold');
      pdf.setTextColor(30, 64, 175); // Blue color
      
      const bookTitle = result.title || result.manuscript_title;
      const title = `${bookTitle} - Viability Analysis`;
      pdf.text(title, leftMargin, yPosition);
      yPosition += 15;
      
      // Market Potential
      if (result.market_potential) {
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(30, 64, 175);
        pdf.text('Market Potential', leftMargin, yPosition);
        yPosition += 7;
        
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        pdf.setTextColor(0, 0, 0);
        
        const marketLines = pdf.splitTextToSize(result.market_potential, pageWidth);
        marketLines.forEach(line => {
          if (yPosition > 275) {
            pdf.addPage();
            yPosition = 20;
          }
          pdf.text(line, leftMargin, yPosition);
          yPosition += 5;
        });
        yPosition += 10;
      }
      
      // Competitive Analysis
      if (result.competitive_analysis) {
        if (yPosition > 220) {
          pdf.addPage();
          yPosition = 20;
        }
        
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(30, 64, 175);
        pdf.text('Competitive Analysis', leftMargin, yPosition);
        yPosition += 7;
        
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        pdf.setTextColor(0, 0, 0);
        
        const compLines = pdf.splitTextToSize(result.competitive_analysis, pageWidth);
        compLines.forEach(line => {
          if (yPosition > 275) {
            pdf.addPage();
            yPosition = 20;
          }
          pdf.text(line, leftMargin, yPosition);
          yPosition += 5;
        });
        yPosition += 10;
      }
      
      // Marketing Strategy
      if (result.marketing_strategy) {
        if (yPosition > 220) {
          pdf.addPage();
          yPosition = 20;
        }
        
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(30, 64, 175);
        pdf.text('Marketing Strategy', leftMargin, yPosition);
        yPosition += 7;
        
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        pdf.setTextColor(0, 0, 0);
        
        const marketingLines = pdf.splitTextToSize(result.marketing_strategy, pageWidth);
        marketingLines.forEach(line => {
          if (yPosition > 275) {
            pdf.addPage();
            yPosition = 20;
          }
          pdf.text(line, leftMargin, yPosition);
          yPosition += 5;
        });
        yPosition += 10;
      }
      
      // Revenue Projections
      if (result.revenue_projections) {
        if (yPosition > 220) {
          pdf.addPage();
          yPosition = 20;
        }
        
        pdf.setFontSize(12);
        pdf.setFont(undefined, 'bold');
        pdf.setTextColor(30, 64, 175);
        pdf.text('Revenue Projections', leftMargin, yPosition);
        yPosition += 7;
        
        pdf.setFontSize(10);
        pdf.setFont(undefined, 'normal');
        pdf.setTextColor(0, 0, 0);
        
        const revenueLines = pdf.splitTextToSize(result.revenue_projections, pageWidth);
        revenueLines.forEach(line => {
          if (yPosition > 275) {
            pdf.addPage();
            yPosition = 20;
          }
          pdf.text(line, leftMargin, yPosition);
          yPosition += 5;
        });
      }
      
      // Footer on all pages
      const pageCount = pdf.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        pdf.setPage(i);
        pdf.setFontSize(8);
        pdf.setTextColor(100, 100, 100);
        pdf.text(`Page ${i} of ${pageCount}`, 105, 287, { align: 'center' });
        pdf.text('Generated by Digital Ink AI - www.DigitalInkAI.com', 105, 292, { align: 'center' });
      }
      
      const filename = `BVADM_${bookTitle.replace(/[^a-z0-9]/gi, '_')}_${new Date().getTime()}.pdf`;
      pdf.save(filename);
      
      toast.success('PDF exported successfully!');
    } catch (error) {
      console.error('PDF export error:', error);
      toast.error('Failed to export PDF');
    }
  };
  
  const handleNewAnalysis = () => {
    setResult(null);
    setError('');
    setFormData({
      manuscript_title: '',
      manuscript_file: null,
      genre: '',
      target_audience: '',
      publication_goals: '',
      marketing_budget: '',
      timeline: '',
      previous_works: '',
      author_platform: ''
    });
    
    // Clear URL parameters
    const newUrl = new URL(window.location);
    newUrl.searchParams.delete('report');
    window.history.pushState({}, '', newUrl);
    
    // Reset document title
    document.title = 'Book Viability & Digital Marketing Analysis | Digital Ink AI';
    
    // Scroll to top
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };
  
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-purple-50 to-slate-100">
      {/* Header with Logo and Navigation */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <img 
                src="/Digital_Ink_AI_Logo.png" 
                alt="Digital Ink AI Logo" 
                className="h-12 w-auto"
                onError={(e) => {
                  e.target.onerror = null;
                  e.target.src = "https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png";
                }}
              />
            </div>
            <div className="flex items-center gap-3">
              <button
                onClick={() => navigate('/homepage')}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                🏠 Home
              </button>
              <button
                onClick={() => navigate('/user_dashboard')}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-white border border-slate-300 rounded-lg hover:bg-slate-50 transition-colors"
              >
                📊 Dashboard
              </button>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-6xl mx-auto py-8 px-4 sm:px-6 lg:px-8">
        {error && (
          <div className="bg-red-50 border border-red-300 text-red-700 px-6 py-4 rounded-lg mb-6 print:hidden">
            {error}
          </div>
        )}

        {!result ? (
          <div className="space-y-8">
            {/* Service Title and Description */}
            <div className="text-center mb-8">
              <h1 className="text-4xl font-bold text-slate-900 mb-4">
                📊 Book Viability & Digital Marketing Analysis
              </h1>
              <p className="text-lg text-slate-600 max-w-3xl mx-auto leading-relaxed">
                Make data-driven publishing decisions. Comprehensive manuscript analysis combines market research, competitive intelligence, and revenue forecasting. AI evaluates commercial viability, identifies target demographics, analyzes competitor positioning, provides actionable marketing strategies. Maximize return on investment in today's competitive publishing landscape with professional intelligence.
              </p>
            </div>

            {/* Pricing Info */}
            {user && (
              <div className="mb-8 bg-gradient-to-r from-purple-50 to-pink-50 border border-purple-200 rounded-lg p-6">
                <div className="text-center">
                  {user.role === 'subscriber' || user.role === 'admin' || user.role === 'beta_tester' ? (
                    <div>
                      <h3 className="text-xl font-bold text-green-900 mb-2">✨ Unlimited Free Access</h3>
                      <p className="text-green-700">Included with your subscription - analyze as many manuscripts as you need!</p>
                    </div>
                  ) : (
                    <div>
                      <h3 className="text-xl font-bold text-blue-900 mb-2">💎 Standard Service - $12.00</h3>
                      <p className="text-blue-700">Complete viability and marketing analysis charged to your payment method on file</p>
                    </div>
                  )}
                </div>
              </div>
            )}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 print:hidden">
            <div className="lg:col-span-2">
              <div className="bg-white rounded-lg shadow-lg border border-slate-200">
                <div className="p-6 border-b border-slate-200">
                  <div className="text-center mb-4">
                    <img 
                      src="https://customer-assets.emergentagent.com/job_digitalink-ai/artifacts/jspykkbt_Digital_Ink_AI_Logo-rbg.png" 
                      alt="Digital Ink AI" 
                      className="h-10 mx-auto mb-3"
                    />
                    <h2 className="text-2xl font-bold text-slate-900">📊 Manuscript Upload</h2>
                    <p className="text-sm text-slate-600 mt-1">Powered by Digital Ink AI</p>
                  </div>
                </div>

                <div className="p-6">
                  <form onSubmit={handleSubmit} className="space-y-6">
                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Manuscript Title *</label>
                      <input
                        type="text"
                        name="manuscript_title"
                        value={formData.manuscript_title}
                        onChange={handleInputChange}
                        placeholder="e.g., The Future of Digital Marketing"
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        required
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Upload Manuscript *</label>
                      <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                        <input
                          type="file"
                          name="manuscript_file"
                          onChange={handleFileChange}
                          accept=".pdf,.doc,.docx,.txt"
                          className="hidden"
                          id="manuscript_upload"
                          required
                        />
                        <label htmlFor="manuscript_upload" className="cursor-pointer">
                          <div className="text-4xl mb-4">📄</div>
                          <p className="text-lg font-medium text-gray-700">Click to upload your manuscript</p>
                          <p className="text-sm text-gray-500 mt-2">PDF, Word (.doc, .docx), or Text files accepted</p>
                          <p className="text-xs text-gray-400 mt-1">Maximum file size: 50MB</p>
                        </label>
                        {formData.manuscript_file && (
                          <p className="text-green-600 mt-3">✓ File selected: {formData.manuscript_file.name}</p>
                        )}
                      </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Genre</label>
                        <select
                          name="genre"
                          value={formData.genre}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="">Select Genre...</option>
                          <option value="Fiction">Fiction</option>
                          <option value="Non-Fiction">Non-Fiction</option>
                          <option value="Business">Business</option>
                          <option value="Self-Help">Self-Help</option>
                          <option value="Science Fiction">Science Fiction</option>
                          <option value="Fantasy">Fantasy</option>
                          <option value="Romance">Romance</option>
                          <option value="Mystery/Thriller">Mystery/Thriller</option>
                          <option value="Biography">Biography</option>
                          <option value="Other">Other</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Target Audience</label>
                        <input
                          type="text"
                          name="target_audience"
                          value={formData.target_audience}
                          onChange={handleInputChange}
                          placeholder="e.g., Business professionals, Young adults"
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        />
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Publication Goals</label>
                      <textarea
                        name="publication_goals"
                        value={formData.publication_goals}
                        onChange={handleInputChange}
                        rows={4}
                        placeholder="Describe your goals for this book (e.g., establish expertise, generate leads, bestseller status..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      />
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Marketing Budget Range</label>
                        <select
                          name="marketing_budget"
                          value={formData.marketing_budget}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="">Select Budget...</option>
                          <option value="$0-$1,000">$0-$1,000</option>
                          <option value="$1,000-$5,000">$1,000-$5,000</option>
                          <option value="$5,000-$10,000">$5,000-$10,000</option>
                          <option value="$10,000+">$10,000+</option>
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-semibold text-gray-700 mb-2">Publication Timeline</label>
                        <select
                          name="timeline"
                          value={formData.timeline}
                          onChange={handleInputChange}
                          className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                        >
                          <option value="">Select Timeline...</option>
                          <option value="Within 3 months">Within 3 months</option>
                          <option value="3-6 months">3-6 months</option>
                          <option value="6-12 months">6-12 months</option>
                          <option value="12+ months">12+ months</option>
                        </select>
                      </div>
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Previous Works <span className="text-gray-500 font-normal">(Optional)</span></label>
                      <textarea
                        name="previous_works"
                        value={formData.previous_works}
                        onChange={handleInputChange}
                        rows={3}
                        placeholder="List any previously published books or relevant credentials..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-semibold text-gray-700 mb-2">Author Platform <span className="text-gray-500 font-normal">(Optional)</span></label>
                      <textarea
                        name="author_platform"
                        value={formData.author_platform}
                        onChange={handleInputChange}
                        rows={3}
                        placeholder="Describe your social media following, email list, speaking experience, etc..."
                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500"
                      />
                    </div>

                    <div className="pt-6 border-t border-gray-200">
                      <button
                        type="submit"
                        disabled={loading}
                        className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:bg-gray-400 text-white font-bold py-4 px-6 rounded-lg transition-colors duration-200 shadow-md hover:shadow-lg disabled:opacity-50 disabled:cursor-not-allowed"
                      >
                        {loading ? 'Analyzing Manuscript...' : 'Generate Viability Analysis with Digital Ink AI'}
                      </button>
                    </div>
                  </form>
                </div>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-6">
              {/* Analysis Features */}
              <div className="bg-white rounded-lg shadow-lg border border-slate-200 p-6">
                <h3 className="text-lg font-bold text-slate-900 mb-4">🎯 Analysis Features</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex items-start">
                    <span className="text-purple-500 mr-2">✓</span>
                    <span>Commercial viability assessment</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-purple-500 mr-2">✓</span>
                    <span>Target demographic identification</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-purple-500 mr-2">✓</span>
                    <span>Competitor positioning analysis</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-purple-500 mr-2">✓</span>
                    <span>Revenue forecasting models</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-purple-500 mr-2">✓</span>
                    <span>Marketing strategy recommendations</span>
                  </div>
                  <div className="flex items-start">
                    <span className="text-purple-500 mr-2">✓</span>
                    <span>ROI optimization insights</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
          </div>
        ) : (
          /* Results Display */
          <div className="space-y-8">
            {/* Analysis Details Card */}
            <Card className="bg-gradient-to-r from-purple-50 to-pink-50 border-2 border-purple-200 shadow-lg">
              <CardHeader className="pb-4">
                <CardTitle className="text-2xl font-bold text-slate-900">📋 Analysis Details</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-4">
                  {/* Report Metadata Header */}
                  <div className="border-b pb-6 mb-4 print:border-b-2">
                    <div className="flex justify-between items-start mb-4 print:mb-2">
                      <div>
                        <h2 className="text-2xl font-bold text-slate-900 print:text-3xl">BVADM Analysis Report</h2>
                        <p className="text-slate-600 mt-1">Viability Analysis for {result.title || result.manuscript_title}</p>
                        <p className="text-slate-600 text-sm">by {result.author_name}</p>
                      </div>
                      <div className="flex gap-3 print:hidden">
                        {lastTransactionId && (
                          <button 
                            onClick={() => setShowEmailReceiptModal(true)} 
                            className="hidden md:inline-flex items-center gap-2 px-4 py-2 border border-blue-300 bg-blue-50 text-blue-700 rounded-lg hover:bg-blue-100 transition-colors"
                          >
                            📧 Receipt
                          </button>
                        )}
                        <button onClick={() => window.print()} className="hidden md:inline-flex px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700">
                          🖨️ Print
                        </button>
                        <button onClick={exportToPDF} className="hidden md:inline-flex px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
                          📄 Export PDF
                        </button>
                        <button onClick={handleNewAnalysis} className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700">
                          ✨ New Analysis
                        </button>
                      </div>
                    </div>
                    
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-slate-500">Book Title:</span>
                        <p className="font-semibold text-slate-900">{result.title || result.manuscript_title || 'N/A'}</p>
                      </div>
                      <div>
                        <span className="text-slate-500">Author:</span>
                        <p className="font-semibold text-slate-900">{result.author_name || 'N/A'}</p>
                      </div>
                      {result.subtitle && (
                        <div>
                          <span className="text-slate-500">Subtitle:</span>
                          <p className="font-semibold text-slate-900">{result.subtitle}</p>
                        </div>
                      )}
                      <div>
                        <span className="text-slate-500">Service:</span>
                        <p className="font-semibold text-slate-900">Book Value Analysis</p>
                      </div>
                    </div>
                  </div>
                  
                  {/* Prominent Title and Author */}
                  <div className="bg-white p-4 rounded-lg shadow-sm text-center">
                    <h2 className="text-2xl font-bold text-slate-900 mb-2">{result.title || result.manuscript_title}</h2>
                    {result.subtitle && (
                      <p className="text-lg text-slate-700 italic mb-2">{result.subtitle}</p>
                    )}
                    <p className="text-xl text-slate-800 mb-3">by {result.author_name}</p>
                    
                    {/* Publisher */}
                    {result.publisher && (
                      <div className="mt-4 pt-3 border-t border-slate-200">
                        <span className="text-sm text-slate-600 font-semibold">Publisher: </span>
                        <span className="text-base text-slate-800">{result.publisher}</span>
                      </div>
                    )}
                  </div>
                  
                  {/* Book Statistics */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    {result.word_count && (
                      <div className="bg-white p-4 rounded-lg shadow-sm text-center">
                        <div className="text-2xl font-bold text-purple-600">{result.word_count.toLocaleString()}</div>
                        <div className="text-sm text-slate-600 mt-1">Word Count</div>
                      </div>
                    )}
                    {result.page_count && (
                      <div className="bg-white p-4 rounded-lg shadow-sm text-center">
                        <div className="text-2xl font-bold text-purple-600">{result.page_count}</div>
                        <div className="text-sm text-slate-600 mt-1">Est. Pages</div>
                      </div>
                    )}
                    {result.genre && (
                      <div className="bg-white p-4 rounded-lg shadow-sm text-center">
                        <div className="text-lg font-bold text-purple-600">{result.genre}</div>
                        <div className="text-sm text-slate-600 mt-1">Genre</div>
                      </div>
                    )}
                    {result.subgenre && (
                      <div className="bg-white p-4 rounded-lg shadow-sm text-center">
                        <div className="text-lg font-bold text-purple-600">{result.subgenre}</div>
                        <div className="text-sm text-slate-600 mt-1">Sub-Genre</div>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Scores Section */}
            <Card className="bg-gradient-to-br from-purple-50 via-white to-pink-50 shadow-xl border-2 border-purple-200">
              <CardContent className="p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {/* Overall Score */}
                  {result.overall_score !== null && result.overall_score !== undefined && (
                    <div className="bg-white rounded-xl p-6 shadow-md border border-purple-100">
                      <div className="text-sm font-semibold text-purple-600 uppercase tracking-wide mb-2">Overall Score</div>
                      <div className="text-4xl font-bold text-purple-900">{result.overall_score}/10</div>
                    </div>
                  )}

                  {/* Viability Score */}
                  {result.viability_score !== null && result.viability_score !== undefined && (
                    <div className="bg-white rounded-xl p-6 shadow-md border border-blue-100">
                      <div className="text-sm font-semibold text-blue-600 uppercase tracking-wide mb-2">Viability Score</div>
                      <div className="text-4xl font-bold text-blue-900">{result.viability_score}/10</div>
                    </div>
                  )}

                  {/* Technical Score */}
                  {result.technical_score !== null && result.technical_score !== undefined && (
                    <div className="bg-white rounded-xl p-6 shadow-md border border-green-100">
                      <div className="text-sm font-semibold text-green-600 uppercase tracking-wide mb-2">Technical Score</div>
                      <div className="text-4xl font-bold text-green-900">{result.technical_score}/10</div>
                    </div>
                  )}

                  {/* Content Quality */}
                  {result.content_quality !== null && result.content_quality !== undefined && (
                    <div className="bg-white rounded-xl p-6 shadow-md border border-amber-100">
                      <div className="text-sm font-semibold text-amber-600 uppercase tracking-wide mb-2">Content Quality</div>
                      <div className="text-4xl font-bold text-amber-900">{result.content_quality}/10</div>
                    </div>
                  )}

                  {/* Development Stage */}
                  {result.development_stage && (
                    <div className="bg-white rounded-xl p-6 shadow-md border border-pink-100">
                      <div className="text-sm font-semibold text-pink-600 uppercase tracking-wide mb-2">Development Stage</div>
                      <div className="text-2xl font-bold text-pink-900">{result.development_stage}</div>
                    </div>
                  )}

                  {/* Manuscript Info */}
                  <div className="bg-white rounded-xl p-6 shadow-md border border-slate-100">
                    <div className="text-sm font-semibold text-slate-600 uppercase tracking-wide mb-2">Manuscript Info</div>
                    <div className="space-y-1">
                      {result.word_count && (
                        <div className="text-lg font-semibold text-slate-700">
                          {result.word_count.toLocaleString()} words
                        </div>
                      )}
                      {result.page_count && (
                        <div className="text-lg font-semibold text-slate-700">
                          {result.page_count} pages
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* 1. Narrative Summary */}
            {result.narrative_summary && (
              <Card className="bg-white shadow-lg border border-slate-200">
                <CardHeader className="bg-gradient-to-r from-blue-50 to-indigo-50 border-b">
                  <CardTitle className="text-2xl text-blue-900">📖 Narrative Summary</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="prose max-w-none text-slate-700 leading-relaxed whitespace-pre-wrap">
                    {result.narrative_summary}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* 2. Score Explanations */}
            {result.score_explanations && (
              <Card className="bg-white shadow-lg border border-slate-200">
                <CardHeader className="bg-gradient-to-r from-yellow-50 to-amber-50 border-b">
                  <CardTitle className="text-2xl text-yellow-900">⭐ Score Explanations</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="prose max-w-none text-slate-700 leading-relaxed whitespace-pre-wrap">
                    {result.score_explanations}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* 3. Content Observations */}
            {result.content_observations && (
              <Card className="bg-white shadow-lg border border-slate-200">
                <CardHeader className="bg-gradient-to-r from-pink-50 to-rose-50 border-b">
                  <CardTitle className="text-2xl text-pink-900">🔍 Content Observations</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="prose max-w-none text-slate-700 leading-relaxed whitespace-pre-wrap">
                    {result.content_observations}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* 4. Comparable Titles */}
            {result.comparable_titles && (
              <Card className="bg-white shadow-lg border border-slate-200">
                <CardHeader className="bg-gradient-to-r from-cyan-50 to-sky-50 border-b">
                  <CardTitle className="text-2xl text-cyan-900">📚 Comparable Titles</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="prose max-w-none text-slate-700 leading-relaxed whitespace-pre-wrap">
                    {result.comparable_titles}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* 5. Development Recommendations */}
            {result.development_recommendations && (
              <Card className="bg-white shadow-lg border border-slate-200">
                <CardHeader className="bg-gradient-to-r from-green-50 to-emerald-50 border-b">
                  <CardTitle className="text-2xl text-green-900">✏️ Development Recommendations</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="prose max-w-none text-slate-700 leading-relaxed whitespace-pre-wrap">
                    {result.development_recommendations}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* 6. Market Positioning */}
            {result.market_positioning && (
              <Card className="bg-white shadow-lg border border-slate-200">
                <CardHeader className="bg-gradient-to-r from-purple-50 to-violet-50 border-b">
                  <CardTitle className="text-2xl text-purple-900">🎯 Market Positioning</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="prose max-w-none text-slate-700 leading-relaxed whitespace-pre-wrap">
                    {result.market_positioning}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* 7. Monetization Strategy */}
            {result.monetization_strategy && (
              <Card className="bg-white shadow-lg border border-slate-200">
                <CardHeader className="bg-gradient-to-r from-teal-50 to-cyan-50 border-b">
                  <CardTitle className="text-2xl text-teal-900">💰 Monetization Strategy</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="prose max-w-none text-slate-700 leading-relaxed whitespace-pre-wrap">
                    {result.monetization_strategy}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* 8. Marketing Roadmap */}
            {result.marketing_roadmap && (
              <Card className="bg-white shadow-lg border border-slate-200">
                <CardHeader className="bg-gradient-to-r from-indigo-50 to-blue-50 border-b">
                  <CardTitle className="text-2xl text-indigo-900">🗺️ Marketing Roadmap</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="prose max-w-none text-slate-700 leading-relaxed whitespace-pre-wrap">
                    {result.marketing_roadmap}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* 9. Digital Marketing Assets */}
            {result.digital_marketing_assets && (
              <Card className="bg-white shadow-lg border border-slate-200">
                <CardHeader className="bg-gradient-to-r from-amber-50 to-yellow-50 border-b">
                  <CardTitle className="text-2xl text-amber-900">📱 Digital Marketing Assets</CardTitle>
                </CardHeader>
                <CardContent className="p-6">
                  <div className="prose max-w-none text-slate-700 leading-relaxed whitespace-pre-wrap">
                    {result.digital_marketing_assets}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* Modals */}
        {showPaymentModal && (
          <ServiceChargeModal
            isOpen={showPaymentModal}
            onClose={() => setShowPaymentModal(false)}
            onConfirm={handlePaymentSuccess}
            serviceInfo={paymentInfo}
            user={user}
          />
        )}

        {loading && (
          <ProcessingModal
            isOpen={loading}
            serviceName="Book Viability Analysis & Decision Matrix (BVADM)"
            bookTitle={formData.manuscript_title}
            progress={processingProgress}
            currentStep={currentStep}
            progressSteps={progressSteps}
          />
        )}

        {showEmailReceiptModal && lastTransactionId && (
          <EmailReceiptModal
            isOpen={showEmailReceiptModal}
            onClose={() => setShowEmailReceiptModal(false)}
            transactionId={lastTransactionId}
            userEmail={user?.email}
          />
        )}
      </main>
    </div>
  );
}

export default BVADMPage;